import { useState } from "react";
import { View, Text, Spinner, SettingsList } from "../base";
import { Icon } from "../base/Icon";
import { CustomVoiceCard } from "../voices/CustomVoiceCard";
import { CreateVoiceCloneDialogue } from "../voices/CreateVoiceCloneDialogue";
import { OrganizationCustomVoice } from "../../redux/reducers/organizationCustomVoicesSlice";
import { useAudioSamples } from "../../hooks/useAudioSamples";
import { OrganizationSettingsTab } from "./OrganizationSettingTab";

export type CustomVoicesCardProps = {
  customVoices: OrganizationCustomVoice[];
  isLoading: boolean;
  onCreateVoice: (params: { name: string; description: string; language: string; file: File }) => Promise<void>;
  onUpdateVoice: (id: string, params: { name: string; description: string }) => void;
  onDeleteVoice: (id: string) => void;
  className?: string;
};

export const CustomVoicesCard = ({ customVoices, isLoading, onCreateVoice, onUpdateVoice, onDeleteVoice, className }: CustomVoicesCardProps) => {
  const [showCreateVoiceDialog, setShowCreateVoiceDialog] = useState(false);

  const audioSamples = useAudioSamples();
  const sortedVoices = [...customVoices].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  const onClickCreateVoice = () => {
    setShowCreateVoiceDialog(true);
  };

  const onCreateDialogClose = () => {
    setShowCreateVoiceDialog(false);
  };

  return (
    <OrganizationSettingsTab
      title="Custom Voices"
      count={customVoices.length}
    >
      {isLoading ? (
        <View className="flex items-center justify-center py-4">
          <Spinner className="!text-brand" />
        </View>
      ) : (
        <SettingsList>
          {sortedVoices.map((voice) => (
            <CustomVoiceCard
              key={voice.id}
              voice={voice}
              isActive={audioSamples.activeSampleKey === `${voice.id}-preview`}
              onPlaySample={audioSamples.playSample}
              onUpdateVoice={onUpdateVoice}
              onDeleteVoice={onDeleteVoice}
            />
          ))}
          <button
            onClick={onClickCreateVoice}
            className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
          >
            <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={14}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Create voice
            </Text>
          </button>
        </SettingsList>
      )}

      <CreateVoiceCloneDialogue
        isOpen={showCreateVoiceDialog}
        onClose={onCreateDialogClose}
        onCreateVoice={onCreateVoice}
        audioSamples={audioSamples}
      />
    </OrganizationSettingsTab>
  );
};
